import { useState, useEffect } from "react";
import { useAuth, useLanguage, API } from "../App";
import { AppLayout } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Clock, Plus, Calendar, Check } from "lucide-react";
import { toast } from "sonner";

export const TimeReportPage = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    project_id: "",
    hours: "",
    date: today,
    entry_type: "regular",
    notes: ""
  });

  useEffect(() => {
    fetchProjects();
    fetchEntries();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setProjects(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await fetch(`${API}/time-entries`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });
      if (res.ok) {
        setEntries(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch entries:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_id || !formData.hours) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/time-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          hours: parseFloat(formData.hours)
        })
      });
      
      if (res.ok) {
        toast.success(language === "sv" ? "Tid rapporterad!" : "Time reported!");
        setFormData({ ...formData, hours: "", notes: "" });
        fetchEntries();
      } else {
        toast.error(language === "sv" ? "Kunde inte rapportera tid" : "Failed to report time");
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const quickHours = [1, 2, 4, 6, 8];
  const entryTypes = [
    { value: "regular", label: language === "sv" ? "Ordinarie" : "Regular" },
    { value: "overtime", label: language === "sv" ? "Övertid" : "Overtime" },
    { value: "machine", label: language === "sv" ? "Maskintid" : "Machine time" },
    { value: "absence", label: language === "sv" ? "Frånvaro" : "Absence" }
  ];

  const todayEntries = entries.filter(e => e.date === today);
  const todayTotal = todayEntries.reduce((sum, e) => sum + e.hours, 0);
  const weekTotal = entries.reduce((sum, e) => sum + e.hours, 0);

  return (
    <AppLayout>
      <div className="p-4 lg:p-6 space-y-6 pb-20 lg:pb-6" data-testid="time-report-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
            {language === "sv" ? "Tidrapportering" : "Time Report"}
          </h1>
          <p className="text-slate-600 mt-1">
            {language === "sv" ? "Rapportera din arbetstid snabbt och enkelt" : "Report your work time quickly and easily"}
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {todayTotal}h
                  </p>
                  <p className="text-sm text-slate-600">{language === "sv" ? "Idag" : "Today"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                    {weekTotal}h
                  </p>
                  <p className="text-sm text-slate-600">{language === "sv" ? "Denna vecka" : "This week"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Entry Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Snabbrapportering" : "Quick Report"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Project Selection */}
              <div className="space-y-2">
                <Label className="form-label">{language === "sv" ? "Projekt" : "Project"} *</Label>
                <Select 
                  value={formData.project_id} 
                  onValueChange={(v) => setFormData({...formData, project_id: v})}
                >
                  <SelectTrigger className="h-12" data-testid="time-project-select">
                    <SelectValue placeholder={language === "sv" ? "Välj projekt" : "Select project"} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quick Hours */}
              <div className="space-y-2">
                <Label className="form-label">{language === "sv" ? "Timmar" : "Hours"} *</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {quickHours.map(h => (
                    <Button
                      key={h}
                      type="button"
                      variant={formData.hours === String(h) ? "default" : "outline"}
                      className="h-12 w-12"
                      onClick={() => setFormData({...formData, hours: String(h)})}
                      data-testid={`quick-hour-${h}`}
                    >
                      {h}h
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  value={formData.hours}
                  onChange={(e) => setFormData({...formData, hours: e.target.value})}
                  placeholder={language === "sv" ? "Eller ange timmar" : "Or enter hours"}
                  className="h-12"
                  step="0.5"
                  min="0"
                  data-testid="time-hours-input"
                />
              </div>

              {/* Entry Type */}
              <div className="space-y-2">
                <Label className="form-label">{language === "sv" ? "Typ" : "Type"}</Label>
                <Select 
                  value={formData.entry_type} 
                  onValueChange={(v) => setFormData({...formData, entry_type: v})}
                >
                  <SelectTrigger className="h-12" data-testid="time-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {entryTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label className="form-label">{language === "sv" ? "Datum" : "Date"}</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="h-12"
                  data-testid="time-date-input"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="form-label">{language === "sv" ? "Anteckningar" : "Notes"}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder={language === "sv" ? "Vad arbetade du med?" : "What did you work on?"}
                  rows={2}
                  data-testid="time-notes-input"
                />
              </div>

              {/* Submit */}
              <Button 
                type="submit" 
                className="w-full h-14 text-lg" 
                disabled={!formData.project_id || !formData.hours || submitting}
                data-testid="time-submit-btn"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {language === "sv" ? "Sparar..." : "Saving..."}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    {language === "sv" ? "Rapportera tid" : "Report time"}
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Recent Entries */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Senaste rapporter" : "Recent Reports"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : entries.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                {language === "sv" ? "Inga rapporter ännu" : "No reports yet"}
              </p>
            ) : (
              <div className="space-y-3">
                {entries.slice(0, 10).map((entry) => {
                  const project = projects.find(p => p.project_id === entry.project_id);
                  return (
                    <div 
                      key={entry.entry_id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      data-testid={`time-entry-${entry.entry_id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{project?.name || entry.project_id}</p>
                        <p className="text-sm text-slate-500">{entry.date}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{entry.hours}h</p>
                        <p className="text-xs text-slate-500 capitalize">{entry.entry_type}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};
